import { useState } from "react";

export type CookieCategoryId = "functional" | "analytics";

export type CookieConsentTranslations = {
	heading: string;
	body: string;
	acceptAllLabel: string;
	rejectAllLabel: string;
	preferencesLabel: string;
	saveLabel: string;
	categories: Record<CookieCategoryId, { label: string; description: string }>;
};

export type CookieConsentValues = {
	functional: boolean;
	analytics: boolean;
};

type Props = {
	open: boolean;
	translations: CookieConsentTranslations;
	values: CookieConsentValues;
	onAcceptAll: () => void;
	onRejectAll: () => void;
	onSave: (values: CookieConsentValues) => void;
};

const CATEGORY_IDS: CookieCategoryId[] = ["functional", "analytics"];

export function CookieConsentBanner({
	open,
	translations: t,
	values,
	onAcceptAll,
	onRejectAll,
	onSave,
}: Props) {
	const [expanded, setExpanded] = useState(false);
	const [choices, setChoices] = useState<CookieConsentValues>(values);

	if (!open) return null;

	return (
		<div
			role="dialog"
			aria-label={t.heading}
			className="fixed inset-x-0 bottom-0 z-[1200] border-t border-[var(--border-strong)] bg-[var(--surface-default)] p-4 shadow-[var(--shadow-popup)] sm:p-5"
		>
			<div className="mx-auto flex max-w-[720px] flex-col gap-3">
				<div className="flex flex-col gap-1">
					<h2 className="m-0 text-[15px] font-extrabold">{t.heading}</h2>
					<p className="m-0 text-[13.5px] leading-[1.5] text-[var(--content-muted)]">
						{t.body}
					</p>
				</div>

				{expanded && (
					<div className="flex flex-col gap-3 rounded-[var(--radius-md)] bg-[var(--surface-elevated)] p-3.5">
						{CATEGORY_IDS.map((id) => (
							<label
								key={id}
								className="flex items-start gap-2.5 text-[13.5px]"
							>
								<input
									type="checkbox"
									checked={choices[id]}
									onChange={(e) =>
										setChoices({ ...choices, [id]: e.target.checked })
									}
									className="mt-0.5"
								/>
								<span className="flex flex-col gap-0.5">
									<span className="font-bold">{t.categories[id].label}</span>
									<span className="text-[var(--content-muted)]">
										{t.categories[id].description}
									</span>
								</span>
							</label>
						))}
					</div>
				)}

				<div className="flex flex-wrap gap-2.5">
					{expanded ? (
						<button
							type="button"
							onClick={() => onSave(choices)}
							className="h-10 rounded-[var(--radius-sm)] bg-[var(--surface-inverted)] px-4 text-[13.5px] font-bold text-[var(--content-inverted)]"
						>
							{t.saveLabel}
						</button>
					) : (
						<>
							<button
								type="button"
								onClick={onAcceptAll}
								className="h-10 rounded-[var(--radius-sm)] bg-[var(--surface-inverted)] px-4 text-[13.5px] font-bold text-[var(--content-inverted)]"
							>
								{t.acceptAllLabel}
							</button>
							<button
								type="button"
								onClick={onRejectAll}
								className="h-10 rounded-[var(--radius-sm)] border border-[var(--border-strong)] px-4 text-[13.5px] font-bold"
							>
								{t.rejectAllLabel}
							</button>
							<button
								type="button"
								onClick={() => setExpanded(true)}
								className="h-10 rounded-[var(--radius-sm)] px-4 text-[13.5px] font-bold text-[var(--content-muted)]"
							>
								{t.preferencesLabel}
							</button>
						</>
					)}
				</div>
			</div>
		</div>
	);
}
